import { DatePicker, Flex, Form, Grid, Link, Radio, Select, Text } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { domain } from 'modules/data';
import moment from 'moment';
import { FC, memo, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormValues } from '../BudgetSettingsPopupContent/BudgetSettingsPopupContent.types';
import {
    checkPeriodFirstMonthName,
    checkPeriodFromName,
    checkPeriodName,
    checkPeriodTillName,
} from './CheckPeriodFormPart.constants';
import { messages } from './CheckPeriodFormPart.messages';
import { ShiftedFlex } from './CheckPeriodFormPart.styled';
import { CheckPeriodFormPartProps } from './CheckPeriodFormPart.types';

const minDateForStartCustomPeriod = moment().subtract(60, 'month').toDate();
const maxDateForStartCustomPeriod = moment().add(6, 'month').toDate();

const CheckPeriodFormPart: FC<CheckPeriodFormPartProps> = memo((props) => {
    const { origin, learnMoreLink } = props;

    const monthsItems = hooks.useMonthsItems();

    const formContext = useFormContext<FormValues>();
    const [checkPeriodValue, checkPeriodFromValue, checkPeriodTillValue] = formContext.watch([
        checkPeriodName,
        checkPeriodFromName,
        checkPeriodTillName,
    ]);

    const { setValue, trigger, clearErrors } = formContext;

    const minDateForEndCustomPeriod = checkPeriodFromValue;
    const maxDateForEndCustomPeriod = useMemo(
        () => (minDateForEndCustomPeriod ? moment(minDateForEndCustomPeriod).add(60, 'month').toDate() : undefined),
        [minDateForEndCustomPeriod]
    );

    /**
     * Check if till date is inside available range and reset it if not
     */
    useEffect(() => {
        if (!checkPeriodTillValue || !minDateForEndCustomPeriod) return;

        const isTillDateInsideAvailableRange = moment(checkPeriodTillValue).isBetween(
            minDateForEndCustomPeriod,
            maxDateForEndCustomPeriod,
            undefined,
            '[]'
        );

        if (!isTillDateInsideAvailableRange) {
            const value = moment(checkPeriodTillValue).isBefore(minDateForEndCustomPeriod)
                ? minDateForEndCustomPeriod
                : moment(checkPeriodTillValue).isAfter(maxDateForEndCustomPeriod)
                  ? maxDateForEndCustomPeriod
                  : undefined;

            setValue(checkPeriodTillName, value);
            trigger(checkPeriodTillName);
        } else {
            clearErrors(checkPeriodTillName);
        }
    }, [
        checkPeriodFromValue,
        checkPeriodTillValue,
        clearErrors,
        maxDateForEndCustomPeriod,
        minDateForEndCustomPeriod,
        setValue,
        trigger,
    ]);

    return (
        <Form.Part>
            <Grid gridTemplateColumns='auto max-content'>
                <Text font='headline' as='h4' fontSize='xxsmall' spacing='0 0 16'>
                    {messages.headline}
                </Text>

                {learnMoreLink && (
                    <Link font='body' href={learnMoreLink} external>
                        {messages.learnMore}
                    </Link>
                )}
            </Grid>

            <Radio.Group name={checkPeriodName} block>
                <Radio value={domain.BudgetCheckPeriod.Monthly}>{messages.oneMonthPeriod}</Radio>

                <Radio value={domain.BudgetCheckPeriod.Quarterly}>{messages.threeMonthPeriod}</Radio>

                <Radio value={domain.BudgetCheckPeriod.Semiannually}>{messages.sixMonthsPeriod}</Radio>

                <Radio value={domain.BudgetCheckPeriod.Annually}>{messages.yearlyPeriod}</Radio>

                <Radio value={domain.BudgetCheckPeriod.YearToDate}>{messages.yearToDate}</Radio>

                <Flex container wrap={false} spacing='8' alignItems='center'>
                    <Flex size={0} grow={0}>
                        <Radio value={domain.BudgetCheckPeriod.AnnuallyWithCustomFirstMonth}>
                            {messages.yearWithSelectedStartMonth}
                        </Radio>
                    </Flex>

                    {checkPeriodValue === domain.BudgetCheckPeriod.AnnuallyWithCustomFirstMonth && (
                        <ShiftedFlex size={0} grow={0}>
                            <Select
                                items={monthsItems}
                                name={checkPeriodFirstMonthName}
                                required={checkPeriodValue === domain.BudgetCheckPeriod.AnnuallyWithCustomFirstMonth}
                                size='small'
                                clearable={false}
                                placeholder={messages.monthSelectPlaceholder}
                            />
                        </ShiftedFlex>
                    )}
                </Flex>

                <Flex container wrap={false} spacing='8' alignItems='center'>
                    <Flex size={0} grow={0}>
                        <Radio value={domain.BudgetCheckPeriod.YearToDateWithCustomFirstMonth}>
                            {messages.yearToDateWithCustomFirstMonth}
                        </Radio>
                    </Flex>

                    {checkPeriodValue === domain.BudgetCheckPeriod.YearToDateWithCustomFirstMonth && (
                        <ShiftedFlex size={0} grow={0}>
                            <Select
                                items={monthsItems}
                                name={checkPeriodFirstMonthName}
                                required={checkPeriodValue === domain.BudgetCheckPeriod.YearToDateWithCustomFirstMonth}
                                size='small'
                                clearable={false}
                                placeholder={messages.monthSelectPlaceholder}
                            />
                        </ShiftedFlex>
                    )}
                </Flex>

                {(!origin || origin === domain.BudgetOrigin.Manual) && (
                    <Flex container wrap={false} spacing='8' alignItems='center'>
                        <Flex size={0} grow={0}>
                            <Radio value={domain.BudgetCheckPeriod.Custom}>{messages.customPeriod}</Radio>
                        </Flex>

                        {checkPeriodValue === domain.BudgetCheckPeriod.Custom && (
                            <>
                                <Flex size={0} grow={0}>
                                    {messages.customMonthPeriodFrom}
                                </Flex>

                                <ShiftedFlex size={0} grow={0}>
                                    <DatePicker
                                        name={checkPeriodFromName}
                                        required={checkPeriodValue === domain.BudgetCheckPeriod.Custom}
                                        size='small'
                                        clearable={false}
                                        placeholder={messages.monthSelectPlaceholder}
                                        view='year'
                                        minDate={minDateForStartCustomPeriod}
                                        maxDate={maxDateForStartCustomPeriod}
                                    />
                                </ShiftedFlex>

                                <Flex size={0} grow={0}>
                                    {messages.customMonthPeriodTo}
                                </Flex>

                                <ShiftedFlex size={0} grow={0}>
                                    <DatePicker
                                        name={checkPeriodTillName}
                                        required={checkPeriodValue === domain.BudgetCheckPeriod.Custom}
                                        size='small'
                                        clearable={false}
                                        placeholder={messages.monthSelectPlaceholder}
                                        view='year'
                                        minDate={minDateForEndCustomPeriod}
                                        maxDate={maxDateForEndCustomPeriod}
                                    />
                                </ShiftedFlex>
                            </>
                        )}
                    </Flex>
                )}
            </Radio.Group>
        </Form.Part>
    );
});

CheckPeriodFormPart.displayName = 'CheckPeriodFormPart';

export default CheckPeriodFormPart;
